import { Component, ChangeDetectionStrategy, OnInit, ViewChild, ElementRef, OnDestroy, inject, AfterViewInit, NgZone } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { partners } from '@app/partners/data/partners';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { WindowService } from '@shared/window';
import { Autoplay, Pagination } from 'swiper/modules';
import { Partner as _Partner } from '@app/partners/model/partner.model';
import Swiper from 'swiper';
import { LocalizePipe } from '../../../i18n/pipes/lozalize.pipe';

interface Partner extends _Partner {
    slug: string;
}

@Component({
    selector: 'app-our-partners-logos',
    templateUrl: './our-partners-logos.component.html',
    styleUrls: ['./our-partners-logos.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, LocalizePipe],
})
export class OurPartnersLogosComponent implements OnInit, AfterViewInit, OnDestroy {
    private zone = inject(NgZone);
    private activatedRoute = inject(ActivatedRoute);
    private router = inject(Router);
    private translateService = inject(TranslateService);
    private windowService = inject(WindowService);

    @ViewChild('swiper') swiperRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperPagination') swiperPaginationRef!: ElementRef<HTMLDivElement>;

    readonly isClient = this.windowService.isClient;
    partners!: Partner[];

    private swiper?: Swiper;

    ngOnInit(): void {
        this.partners = Object.keys(partners)
            .map((partner) => ({
                ...partners[partner],
                ...{ slug: partner },
            }))
            .filter((partner) => partner.availableLanguages.includes(this.translateService.currentLang))
            .sort(() => 0.5 - Math.random());

        this.activatedRoute.params
            .pipe(
                tap(() => {
                    void this.router.navigate([this.router.url]);
                }),
            )
            .subscribe();
    }

    ngAfterViewInit() {
        if (this.windowService.isBrowser) {
            this.initSwiper();
        }
    }

    ngOnDestroy(): void {
        this.swiper?.destroy();
    }

    private initSwiper() {
        this.zone.runOutsideAngular(() => {
            this.swiper = new Swiper(this.swiperRef.nativeElement, {
                modules: [Pagination, Autoplay],
                shortSwipes: false,
                autoplay: {
                    delay: 4000,
                    disableOnInteraction: false,
                },
                pagination: {
                    el: this.swiperPaginationRef.nativeElement,
                    type: 'bullets',
                    clickable: true,
                },
                centeredSlides: true,
                centeredSlidesBounds: true,
                loop: true,
                speed: 1500,
                watchOverflow: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                breakpoints: {
                    0: {
                        slidesPerView: 1,
                        shortSwipes: true,
                        speed: 900,
                    },
                    576: {
                        slidesPerView: 2,
                        shortSwipes: true,
                        speed: 900,
                    },
                    768: {
                        slidesPerView: 3,
                        speed: 900,
                    },
                    992: {
                        slidesPerView: 4,
                    },
                    1200: {
                        slidesPerView: 5,
                    },
                },
                threshold: 5,
            });
        });
    }
}
